import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/auth/AuthContext";
import { MANAGEMENT_URL } from "@/config/env";

export interface FeedEvent {
  /** Unique key für React; lokal generiert weil SSE-Events keine ID haben. */
  key: number;
  subject: string;
  ts: string;
  payload: unknown;
}

export type FeedStatus = "connecting" | "open" | "closed" | "error";

interface Options {
  /** NATS-Subject-Patterns. Default: serverseitig agent.*.lifecycle.> + master.peer.*.event. */
  topics?: readonly string[];
  /** Max Anzahl Events im State (FIFO). Default 100. */
  bufferSize?: number;
  /** Aktiviert die Subscription. Default true. */
  enabled?: boolean;
}

// M7: EventSource-Wrapper.
// AUTH-Trade-off: Token wird als Query-Param mitgesendet (EventSource-Spec
// erlaubt keine Custom-Header). Trade-off im Master-Endpoint dokumentiert.
//
// Reconnect: Browser-native EventSource reconnected automatisch — wir
// resetten den Status auf "connecting" wenn das passiert. Verpasste Events
// während Disconnect sind nicht ersetzbar (live-only in M7).
export function useEventStream(opts: Options = {}): {
  events: FeedEvent[];
  status: FeedStatus;
  ready: { topics: string[] } | null;
  clear: () => void;
} {
  const { topics, bufferSize = 100, enabled = true } = opts;
  const { session } = useAuth();
  const [events, setEvents] = useState<FeedEvent[]>([]);
  const [status, setStatus] = useState<FeedStatus>("closed");
  const [ready, setReady] = useState<{ topics: string[] } | null>(null);
  const counterRef = useRef(0);

  // Topics als Memo-stable String damit useEffect nicht jeden Render reconnected
  const topicsKey = topics ? [...topics].sort().join(",") : "";

  useEffect(() => {
    if (!enabled || !session?.accessToken) {
      setStatus("closed");
      return;
    }

    // Base-Argument nötig: im same-origin-Build ist MANAGEMENT_URL leer (relative Pfade) —
    // new URL("/events") OHNE Base wirft "Invalid URL" und crasht die App beim Mount.
    const url = new URL(`${MANAGEMENT_URL}/events`, window.location.origin);
    url.searchParams.set("token", session.accessToken);
    if (topicsKey.length > 0) url.searchParams.set("topics", topicsKey);

    setStatus("connecting");
    const es = new EventSource(url.toString());

    es.onopen = () => setStatus("open");
    es.onerror = () => {
      // Browser-native reconnect läuft; wir signalisieren error+connecting
      setStatus("error");
    };

    es.addEventListener("ready", (e: MessageEvent) => {
      try {
        const data = JSON.parse(e.data);
        setReady({ topics: Array.isArray(data.topics) ? data.topics : [] });
      } catch {
        // ignore
      }
    });

    es.onmessage = (e: MessageEvent) => {
      try {
        const data = JSON.parse(e.data);
        counterRef.current += 1;
        const evt: FeedEvent = {
          key: counterRef.current,
          subject: typeof data.subject === "string" ? data.subject : "?",
          ts: typeof data.ts === "string" ? data.ts : new Date().toISOString(),
          payload: data.payload,
        };
        setEvents((prev) => {
          const next = [evt, ...prev];
          return next.length > bufferSize ? next.slice(0, bufferSize) : next;
        });
      } catch {
        // ignore malformed message
      }
    };

    return () => {
      es.close();
      setStatus("closed");
    };
  }, [enabled, session?.accessToken, topicsKey, bufferSize]);

  return {
    events,
    status,
    ready,
    clear: () => setEvents([]),
  };
}
